import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Flag, Calendar, MapPin, ArrowRight, CircleAlert } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { Icon } from '@/components/ui/icon'
import { AuthService } from '@/services/core/auth'
import { ElectionsApi } from '@/services/political/electionsApi'
import { Election } from '../../types/models'
import { AppShell } from '../../components/AppShell'
import { Card } from '@/components/ui/card'
import { formatDate } from '../../utils/format'

type ElectionMeta = {
  label: string
  accent: string
  soft: string
  icon: string
}

function getElectionMeta(
  dateStr: string,
  testOnly: boolean,
  t: (key: string, opts?: Record<string, unknown>) => string,
): ElectionMeta {
  if (testOnly)
    return {
      label: t('elections.testOnly'),
      accent: '#94a3b8',
      soft: 'rgba(148,163,184,0.1)',
      icon: 'pi-wrench',
    }
  if (!dateStr)
    return { label: '—', accent: '#94a3b8', soft: 'rgba(148,163,184,0.1)', icon: 'pi-flag' }

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const d = new Date(dateStr)
  const diff = Math.ceil((d.getTime() - today.getTime()) / 86_400_000)

  if (diff < 0)
    return {
      label: t('elections.concluded'),
      accent: '#94a3b8',
      soft: 'rgba(148,163,184,0.08)',
      icon: 'pi-flag',
    }
  if (diff === 0)
    return {
      label: t('elections.today'),
      accent: '#16a34a',
      soft: 'rgba(22,163,74,0.08)',
      icon: 'pi-flag-fill',
    }
  if (diff <= 30)
    return {
      label: t('elections.daysAway', { count: diff }),
      accent: '#d97706',
      soft: 'rgba(217,119,6,0.08)',
      icon: 'pi-flag',
    }
  if (diff <= 90)
    return {
      label: t('elections.weeksAway', { count: Math.ceil(diff / 7) }),
      accent: '#2f6df4',
      soft: 'rgba(47,109,244,0.08)',
      icon: 'pi-flag',
    }
  return {
    label: formatDate(d, { month: 'short', year: 'numeric' }),
    accent: '#2f6df4',
    soft: 'rgba(47,109,244,0.06)',
    icon: 'pi-flag',
  }
}

function ElectionCard({ election }: { election: Election }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const meta = getElectionMeta(election.electionDate, election.testOnly, t)
  const formattedDate = election.electionDate
    ? formatDate(election.electionDate, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null

  return (
    <div
      className="elec-card"
      style={{ '--elec-accent': meta.accent, '--elec-soft': meta.soft, cursor: 'pointer' } as React.CSSProperties}
      onClick={() => navigate(`/political/elections/${election.id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && navigate(`/political/elections/${election.id}`)}
    >
      <div className="elec-card__accent-bar" />

      <div className="elec-card__head">
        <div className="elec-card__icon">
          <Icon name={meta.icon} className="size-4" />
        </div>
        <span className="elec-card__status">{meta.label}</span>
      </div>

      <div className="elec-card__name">{election.name}</div>

      {election.description && <div className="elec-card__desc">{election.description}</div>}

      <div className="elec-card__footer">
        <div className="elec-card__chips">
          {formattedDate && (
            <span className="elec-card__chip">
              <Calendar className="size-3" />
              {formattedDate}
            </span>
          )}
          {election.type && <span className="elec-card__chip">{election.type}</span>}
          {election.location && (
            <span className="elec-card__chip">
              <MapPin className="size-3" />
              {election.location}
            </span>
          )}
        </div>
        <ArrowRight className="size-4 elec-card__arrow" />
      </div>
    </div>
  )
}

export function ElectionsPage() {
  const { t } = useTranslation()
  const hasToken = AuthService.isAuthenticated()

  const { data: elections = [], isLoading, error } = useQuery({
    queryKey: ['elections'],
    queryFn: ElectionsApi.getAll,
    enabled: hasToken,
  })

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const upcoming = elections.filter((e) => e.electionDate && new Date(e.electionDate) >= today && !e.testOnly)
  const past = elections.filter((e) => e.electionDate && new Date(e.electionDate) < today && !e.testOnly)
  // Undated elections are standing, general-purpose ones rather than a specific vote.
  const general = elections.filter((e) => !e.electionDate && !e.testOnly)
  const testOnly = elections.filter((e) => e.testOnly)

  return (
    <AppShell crumbs={[{ label: t('nav.elections') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('elections.title')}</h1>
            <p>{t('elections.subtitle')}</p>
          </div>
        </div>

        {isLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-md" />
            ))}
          </div>
        )}
        {error && (
          <div className="auth-form__error" style={{ marginBottom: 16 }}>
            <CircleAlert className="size-4" />
            {(error as Error).message}
          </div>
        )}

        {elections.length === 0 && !isLoading ? (
          <Card>
            <div className="empty-state">
              <Flag className="size-6" />
              <div className="empty-state__title">{t('elections.noElections')}</div>
              <div className="empty-state__body">{t('elections.noElectionsFound')}</div>
            </div>
          </Card>
        ) : (
          <>
            {upcoming.length > 0 && (
              <div className="elec-section">
                <div className="elec-section__label">{t('elections.upcoming')}</div>
                <div className="elec-grid">
                  {upcoming.map((e) => (
                    <ElectionCard key={e.id} election={e} />
                  ))}
                </div>
              </div>
            )}
            {past.length > 0 && (
              <div className="elec-section">
                <div className="elec-section__label">{t('elections.past')}</div>
                <div className="elec-grid">
                  {past.map((e) => (
                    <ElectionCard key={e.id} election={e} />
                  ))}
                </div>
              </div>
            )}
            {general.length > 0 && (
              <div className="elec-section">
                <div className="elec-section__label">{t('political.generalPurpose')}</div>
                <div className="elec-grid">
                  {general.map((e) => (
                    <ElectionCard key={e.id} election={e} />
                  ))}
                </div>
              </div>
            )}
            {testOnly.length > 0 && (
              <div className="elec-section">
                <div className="elec-section__label">{t('elections.test')}</div>
                <div className="elec-grid">
                  {testOnly.map((e) => (
                    <ElectionCard key={e.id} election={e} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  )
}
